/* eslint-disable import/no-duplicates */
/**
 * This is a hardcoded environment file that decides the current environment.
 * A decision is made based on the Country that is initialized by the client.
 * All data here is hardcoded, in future this will be converted to APIs.
 */
import type { SupportedLanguages } from '@/plugins/i18n';
import { LANGUAGES } from '@/plugins/i18n';
import { Country } from './config';
import { InitializeOptions } from './types';

declare const GLOBAL_IS_PROD: boolean;
declare const GLOBAL_ENV_NAME: 'FOOTBALL_PROD' | 'FOOTBALL_STAGE' | 'SPORTY_PRODUCTION';

export interface FirebaseSetting {
  auth: string;
  database: string;
  projectId: string;
}

type LanguageSpec = SupportedLanguages[] | 'ALL_LANGUAGES';
export interface HardCodedEnvironment {
  readonly url: string;
  readonly countryToBrand: Record<Country, string>;
  readonly defaultBrand: string;
  readonly firebase: Readonly<FirebaseSetting>;
  readonly userFieldType?: Partial<Record<Country, 'phone'|'email'>>;
  readonly languages: Record<Country, LanguageSpec>;
  readonly appName: string;
}

const DEFAULT_LANGUAGE_MAPPING: Partial<Record<Country, LanguageSpec>> = {
  [Country.KE]: ['en', 'sw'],
  [Country.TZ]: ['en', 'sw'],
  [Country.CM]: ['en', 'fr'],
  [Country.INT]: ['en', 'es', 'fr', 'pt'],
};

/**
 * Enable a list of languages that can be switched from the chatbot.
 * @param languages Optionally override the language specifications.
 */
function enableLanguages (languages: Partial<Record<Country, LanguageSpec>> = {}): Record<Country, LanguageSpec> {
  return {
    [Country.NG]: ['en'],
    [Country.KE]: ['en'],
    [Country.GH]: ['en'],
    [Country.TZ]: ['en'],
    [Country.UG]: ['en'],
    [Country.ZM]: ['en'],
    [Country.INT]: ['en'],
    [Country.CM]: ['en'],
    ...DEFAULT_LANGUAGE_MAPPING,
    ...languages,
  };
}

// Environments
const STAGING: HardCodedEnvironment = {
  url: 'https://stage.pleased2.com/',
  countryToBrand: {
    [Country.NG]: 'sportybetng-uat',
    [Country.KE]: 'sportybetke-uat',
    [Country.GH]: 'sportybetgh-uat',
    [Country.TZ]: 'sportybettz-uat',
    [Country.UG]: 'sportybetug-uat',
    [Country.ZM]: 'sportybetzm-uat',
    [Country.INT]: 'sportybetint-uat',
    [Country.CM]: 'sportybetcm-uat',
  },
  defaultBrand: 'sportybetng-uat',
  firebase: {
    auth: 'pleased-development.firebaseapp.com',
    database: 'https://pleased-development.firebaseio.com',
    projectId: 'pleased-development',
  },
  userFieldType: {
    [Country.INT]: 'email',
  },
  languages: enableLanguages({ [Country.UG]: 'ALL_LANGUAGES', [Country.INT]: ['en', 'pt'] }),
  appName: 'SportyBet',
};

const UAT1: HardCodedEnvironment = {
  url: 'https://uat1.pleased2.com',
  defaultBrand: 'sportybetng-uat1',
  countryToBrand: {
    [Country.NG]: 'sportybetng-uat1',
    [Country.KE]: 'sportybetke-uat1',
    [Country.GH]: 'sportybetgh-uat1',
    [Country.TZ]: 'sportybettz-uat1',
    [Country.UG]: 'sportybetug-uat1',
    [Country.ZM]: 'sportybetzm-uat1',
    [Country.INT]: 'sportybetint-uat1',
    [Country.CM]: 'sportybetint-uat2',
  },
  firebase: {
    auth: 'pleased-uat1.firebaseapp.com',
    database: 'https://pleased-uat1-default-rtdb.firebaseio.com',
    projectId: 'pleased-uat1' },
  userFieldType: {
    [Country.INT]: 'email',
  },
  languages: enableLanguages(),
  appName: 'SportyBet',
};

const UAT2: HardCodedEnvironment = {
  url: 'https://uat2.pleased2.com',
  defaultBrand: 'sportybetng-uat2',
  countryToBrand: {
    [Country.NG]: 'sportybetng-uat2',
    [Country.KE]: 'sportybetke-uat2',
    [Country.GH]: 'sportybetgh-uat2',
    [Country.TZ]: 'sportybettz-uat2',
    [Country.UG]: 'sportybetug-uat2',
    [Country.ZM]: 'sportybetzm-uat2',
    // [Country.INT]: 'sportybetint-uat2',
    [Country.INT]: 'sportybet-int-uat2-sb',
    [Country.CM]: 'sportybet-cm-uat2',
  },
  firebase: {
    auth: 'pleased-uat2.firebaseapp.com',
    database: 'https://pleased-uat2-default-rtdb.firebaseio.com',
    projectId: 'pleased-uat2',
  },
  userFieldType: {
    [Country.INT]: 'email',
  },
  languages: enableLanguages({ [Country.INT]: 'ALL_LANGUAGES' }),
  appName: 'SportyBet',
};

const SPORTY_PRODUCTION: HardCodedEnvironment = {
  url: 'https://sportybet.pleased.com',
  defaultBrand: 'sportybettz',
  countryToBrand: {
    [Country.NG]: 'sportybetng',
    [Country.KE]: 'sportybetke',
    [Country.GH]: 'sportybetgh',
    [Country.TZ]: 'sportybettz',
    [Country.UG]: 'sportybetug',
    [Country.ZM]: 'sportybetzm',
    [Country.INT]: 'sportybetint',
    [Country.CM]: 'sportybetcm',
  },
  firebase: {
    auth: 'pleased-production.firebaseapp.com',
    database: 'https://pleased-production.firebaseio.com',
    projectId: 'pleased-production',
  },
  userFieldType: {
    [Country.INT]: 'email',
  },
  // Disabled languages
  languages: enableLanguages({
    [Country.NG]: ['en'],
    [Country.KE]: ['en'],
    [Country.GH]: ['en'],
    [Country.TZ]: ['en'],
    [Country.UG]: ['en'],
    [Country.ZM]: ['en'],
    [Country.INT]: ['en', 'pt'],
  }),
  appName: 'SportyBet',
};

const FOOTBALL_STAGE: HardCodedEnvironment = {
  url: 'https://football-stage.pleased2.com',
  defaultBrand: 'football-gh-stage',
  countryToBrand: {
    [Country.NG]: 'football-ng-stage',
    [Country.KE]: 'football-ke-stage',
    [Country.GH]: 'football-gh-stage',
    [Country.TZ]: 'football-tz-stage',
    [Country.UG]: 'football-ug-stage',
    [Country.ZM]: 'football-zm-stage',
    // [Country.INT]: 'sportybetint-uat2',
    [Country.INT]: 'football-int-stage',
    [Country.CM]: 'football-cm-stage',
  },
  firebase: {
    auth: 'project-encore-uat.firebaseapp.com',
    database: 'https://project-encore-uat-default-rtdb.firebaseio.com',
    projectId: 'project-encore-uat',
  },
  userFieldType: {
    [Country.INT]: 'email',
  },
  languages: enableLanguages({ [Country.TZ]: 'ALL_LANGUAGES' }),
  appName: 'Football.com',
};

const FOOTBALL_PROD: HardCodedEnvironment = {
  url: 'https://football.pleased.com',
  defaultBrand: 'football-gh',
  countryToBrand: {
    [Country.NG]: 'football-ng',
    [Country.KE]: 'football-ke',
    [Country.GH]: 'football-gh',
    [Country.TZ]: 'football-tz',
    [Country.UG]: 'football-ug',
    [Country.ZM]: 'football-zm',
    [Country.INT]: 'football-int',
    [Country.CM]: 'football-cm',
  },
  firebase: {
    auth: 'project-encore-9f176.firebaseapp.com',
    database: 'https://project-encore-9f176-default-rtdb.firebaseio.com',
    projectId: 'project-encore-9f176',
  },
  userFieldType: {
    [Country.INT]: 'email',
  },
  languages: enableLanguages(),
  appName: 'Football.com',
};

/**
 * Current activated environment. When hard coded environments are removed, then this can be
 * be used as a substitute to set all the values that are globally required by the application.
 * NOTE: Only use `get()` from this module.
 */
const SESSION = {
  env: GLOBAL_IS_PROD ? SPORTY_PRODUCTION : STAGING,
  country: '',
  brandCode: '',
  localeFilter: [] as LanguageSpec,
  enabledLanguages: {} as (typeof LANGUAGES),
  showLanguageSelector: true,
};

const TEST_ENVIRONMENT_ROUTER: Record<Country, HardCodedEnvironment> = {
  [Country.ZM]: UAT1,
  [Country.KE]: UAT2,
  [Country.TZ]: STAGING,
  [Country.NG]: STAGING,
  [Country.GH]: STAGING,
  [Country.UG]: STAGING,
  [Country.INT]: STAGING,
  [Country.CM]: STAGING,
};

const ENVIRONMENTS = { FOOTBALL_PROD, FOOTBALL_STAGE, SPORTY_PRODUCTION };

function getEnvironmentByCountry (country: Country|string) {
  if (ENVIRONMENTS[GLOBAL_ENV_NAME]) return ENVIRONMENTS[GLOBAL_ENV_NAME];

  if (GLOBAL_IS_PROD) {
    return SPORTY_PRODUCTION;
  }
  return TEST_ENVIRONMENT_ROUTER[country as Country];
}

/**
 * Setup the environment, should be called only once.
 */
export function setup (brandCountry: Country | string, options: InitializeOptions) {
  const { enabledLanguages } = options;

  const country = brandCountry as Country;

  // The following variables will enable the usage of `get(variable)` method.
  SESSION.env = getEnvironmentByCountry(country);
  SESSION.country = country;
  // Now the `get()` method is safe to use, the rest of the environment variables can be configured using `get()`

  SESSION.brandCode = get('countryToBrand')[country];
  SESSION.localeFilter = createLocaleFilter({ locales: enabledLanguages, fallback: get('languages')[country] });
  SESSION.enabledLanguages = getEnabledLocales(SESSION.localeFilter);
  SESSION.showLanguageSelector = false; // Object.keys(SESSION.enabledLanguages).length > 1;
}

/**
 * Get the value from the current environment.
 * @param key The key to get.
 */
export function get<K extends keyof typeof SESSION> (key: K): (typeof SESSION)[K];
export function get<K extends keyof HardCodedEnvironment> (key: K): HardCodedEnvironment[K];
export function get<K extends ((keyof HardCodedEnvironment) | keyof(typeof SESSION))> (key: K): K extends keyof HardCodedEnvironment ? HardCodedEnvironment[K] : K extends keyof (typeof SESSION) ? (typeof SESSION)[K] : undefined {
  const value = SESSION[key as keyof typeof SESSION] || SESSION.env[key as keyof HardCodedEnvironment];
  if (value && typeof value === 'object') {
    if (Array.isArray(value)) return [...value] as any;
    return Object.assign({}, value) as any;
  }
  return value as any;
}

export function isProd () {
  return GLOBAL_IS_PROD || SESSION.env === SPORTY_PRODUCTION;
}

export function isTest () {
  return !isProd();
}

export function isLocaleEnabled (locale: string) {
  const toCheck = locale === 'pt-br' ? 'pt' : locale;
  return Boolean(get('enabledLanguages')?.[toCheck as SupportedLanguages]);
}

function getEnabledLocales (filter: LanguageSpec, allLanguages = LANGUAGES) {
  if (filter === 'ALL_LANGUAGES') return allLanguages;

  return Object.fromEntries(
    // For now, force EN language.
    Object.entries(allLanguages).filter(([locale]) => locale === 'en' || filter.includes(locale as SupportedLanguages))
  ) as typeof LANGUAGES;
}

function createLocaleFilter ({ locales = [], fallback }:{locales?: string[], fallback: LanguageSpec}): LanguageSpec {
  if (!locales?.length || !Array.isArray(locales)) return fallback;
  // Normalize the filter. Keep invalid values for now, better for debugging
  return locales.map(s => s?.trim?.() || '').map(l => l === 'pt-br' ? 'pt' : l) as LanguageSpec;
}
